import React from 'react'
import BaseField from './BaseField'
import { SketchPicker } from 'react-color'


export function ColorPicker({ value, setValue, onHide }) {
  const presetColors = [
    { title: "Red", color: "#ba2d2d" },
    { title: "Red Dark", color: "#9e2424" },
    { title: "Orange", color: "#d57b3d" },
    { title: "Orange Dark", color: "#a75923" },
    { title: "Green", color: "#28a745" },
    { title: "Green Dark", color: "#51952e" },
    { title: "Blue Flow", color: "#1ba095" },
    { title: "Blue", color: "#366f9a" },
    { title: "Primary", color: "#0062cd" },
    { title: "Success", color: "#1e7e34" },
    { title: "Danger", color: "#ba2d2d" },
    { title: "Black", color: "#000000" },
    { title: "Gray", color: "#ced4da" },
    { title: "White", color: "#ffffff" },
    { title: "Text", color: "#564f4f" },
    { title: "Body", color: "#eff3f4" },
  ]

  React.useEffect(() => {
    const onEsc = (event) => {
      if (event.keyCode === 27) onHide()
    }
    window.addEventListener("keydown", onEsc, true)
    return () => {
      window.removeEventListener("keydown", onEsc, true)
    }
  }, [])

  return (
    <div className="color-picker-popover">
      <div className="color-picker-cover" onClick={onHide} />
      <SketchPicker
        color={value}
        presetColors={presetColors}
        disableAlpha={true}
        onChange={color => setValue(color.hex)}
      />
    </div>
  )
}

export default function FieldColor(props) {
  /* Input Datalist
   * Expected Props:
   * className = <string> Optional
   * label = <string> Required
   * required = <boolean> Optional
   * name = <string> Required
   * value = <string> Required
   * values = [<string>] Optional
   * placeholder = <string> Optional
   * valid = <boolean> Optional
   * validMessage = <string> Optional
   * invalidMessage = <string> Optional
   * help = <string> Optional
   * helpAboveInput = <boolean> Optional
   * onChange = <function()> Required
   * onBlur = <function()> Optional
   * disabled = <boolean> Optional
   * readOnly = <boolean> Optional
   * prepend = <string> Optional
   * append = <string> Optional
   * horizontal = <boolean> Optional
   * horizontalLabelSize = <int [1-23]> Optional (Default = 6)
   */
  const colorNames = [
    "AliceBlue", "AntiqueWhite", "Aqua", "Aquamarine", "Azure", "Beige", "Bisque", "Black", "BlanchedAlmond",
    "Blue", "BlueViolet", "Brown", "BurlyWood", "CadetBlue", "Chartreuse", "Chocolate", "Coral", "CornflowerBlue",
    "Cornsilk", "Crimson", "Cyan", "DarkBlue", "DarkCyan", "DarkGoldenRod", "DarkGray", "DarkGreen", "DarkGrey",
    "DarkKhaki", "DarkMagenta", "DarkOliveGreen", "DarkOrange", "DarkOrchid", "DarkRed", "DarkSalmon", "DarkSeaGreen",
    "DarkSlateBlue", "DarkSlateGray", "DarkSlateGrey", "DarkTurquoise", "DarkViolet", "DeepPink", "DeepSkyBlue",
    "DimGray", "DimGrey", "DodgerBlue", "FireBrick", "FloralWhite", "ForestGreen", "Fuchsia", "Gainsboro", "GhostWhite",
    "Gold", "GoldenRod", "Gray", "Green", "GreenYellow", "Grey", "HoneyDew", "HotPink", "IndianRed ", "Indigo ",
    "Ivory", "Khaki", "Lavender", "LavenderBlush", "LawnGreen", "LemonChiffon", "LightBlue", "LightCoral", "LightCyan",
    "LightGoldenRodYellow", "LightGray", "LightGreen", "LightGrey", "LightPink", "LightSalmon", "LightSeaGreen",
    "LightSkyBlue", "LightSlateGray", "LightSlateGrey", "LightSteelBlue", "LightYellow", "Lime", "LimeGreen", "Linen",
    "Magenta", "Maroon", "MediumAquaMarine", "MediumBlue", "MediumOrchid", "MediumPurple", "MediumSeaGreen", "MediumSlateBlue", "MediumSpringGreen", "MediumTurquoise", "MediumVioletRed", "MidnightBlue", "MintCream", "MistyRose", "Moccasin",
    "NavajoWhite", "Navy", "OldLace", "Olive", "OliveDrab", "Orange", "OrangeRed", "Orchid", "PaleGoldenRod", "PaleGreen",
    "PaleTurquoise", "PaleVioletRed", "PapayaWhip", "PeachPuff", "Peru", "Pink", "Plum", "PowderBlue", "Purple",
    "RebeccaPurple", "Red", "RosyBrown", "RoyalBlue", "SaddleBrown", "Salmon", "SandyBrown", "SeaGreen", "SeaShell",
    "Sienna", "Silver", "SkyBlue", "SlateBlue", "SlateGray", "SlateGrey", "Snow", "SpringGreen", "SteelBlue", "Tan",
    "Teal", "Thistle", "Tomato", "Turquoise", "Violet", "Wheat", "White", "WhiteSmoke", "Yellow", "YellowGreen"
  ]
  const [showPicker, setShowPicker] = React.useState(false)
  const colorOptions = React.useMemo(() => (
    (props.values || []).concat(colorNames).map((option, key) => <option key={key} value={option}>{option}</option>)
  ), [props.values])

  // RENDER
  return (
    <BaseField
      className={props.className}
      labelClassName={props.labelClassName}
      inputClassName={props.inputClassName}
      label={props.label}
      input={
        <div className="input-group">
          {props.prepend && (
            <div className="input-group-prepend">
              <span className="input-group-text">{props.prepend}</span>
            </div>
          )}
          <input
            ref={props.inputRef}
            list={props.name || "field-color"}
            className={"form-control custom-field-input" + (
              props.valid === true ? " is-valid" : props.valid === false ? " is-invalid" : ""
            )}
            name={props.name}
            placeholder={props.placeholder}
            value={props.value || ""}
            required={props.required && props.valid === false}
            onChange={e => { props.onChange(e); setShowPicker(false) }}
            onBlur={props.onBlur}
            disabled={props.disabled}
            readOnly={props.readOnly}
            autoFocus={props.autoFocus}
          />
          <datalist id={props.name || "field-color"}>
            {colorOptions}
          </datalist>
          <div className="color-picker">
            <div
              className="color-picker-preview"
              style={{ backgroundColor: props.value }}
              title="Select a Color"
              onClick={() => setShowPicker(true)}
            />
            {showPicker && !(props.disabled || props.readOnly) &&
              <ColorPicker
                value={props.value}
                setValue={color => props.onChange({ target: { value: color } })}
                onHide={() => setShowPicker(false)}
              />
            }
          </div>
          {props.append && (
            <div className="input-group-append">
              <span className="input-group-text">{props.append}</span>
            </div>
          )}
        </div>
      }
      disabled={props.disabled}
      readOnly={props.readOnly}
      required={props.required}
      valid={props.valid}
      validMessage={props.validMessage}
      invalidMessage={props.invalidMessage}
      help={props.help}
      helpAboveInput={props.helpAboveInput}
      horizontal={props.horizontal}
      horizontalLabelSize={props.horizontalLabelSize}
    />
  )
}
